import { useEffect, useRef, useState, useCallback } from 'react';
import type { MaterialItem } from '@/api/materialInfo';
import { getWatermarkConfig, type WatermarkConfig } from '@/api/config';
import { getCurrentUser, type CurrentUser } from '@/api/agent';
import WatermarkOverlay from './WatermarkOverlay';
import FeedbackModal from './FeedbackModal';

interface VideoPlayerModalProps {
  material: MaterialItem | null;
  onClose: () => void;
}

export default function VideoPlayerModal({ material, onClose }: VideoPlayerModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const hideControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [buffered, setBuffered] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [wmConfig, setWmConfig] = useState<WatermarkConfig | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const formatTime = (s: number) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const resetHideTimer = useCallback(() => {
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    setShowControls(true);
    hideControlsTimer.current = setTimeout(() => {
      if (playing) setShowControls(false);
    }, 3000);
  }, [playing]);

  useEffect(() => {
    getWatermarkConfig().then(setWmConfig).catch(() => {});
    getCurrentUser().then(setUser).catch(() => {});
  }, []);

  useEffect(() => {
    if (!material) return;
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setShowControls(true);

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === ' ') { e.preventDefault(); togglePlay(); }
      if (e.key === 'f') toggleFullscreen();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
      if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [material]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
    resetHideTimer();
  };

  const toggleFullscreen = () => {
    const container = overlayRef.current?.querySelector('.video-container') as HTMLElement;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen().then(() => setFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setFullscreen(false)).catch(() => {});
    }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
    if (v.buffered.length > 0) {
      setBuffered((v.buffered.end(v.buffered.length - 1) / v.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = progressRef.current?.getBoundingClientRect();
    if (!rect || !videoRef.current || !duration) return;
    const ratio = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) videoRef.current.volume = val;
    setMuted(val === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const newMuted = !muted;
    setMuted(newMuted);
    videoRef.current.muted = newMuted;
  };

  const showTip = (text: string) => { setTooltipText(text); setShowTooltip(true); };
  const hideTip = () => setShowTooltip(false);

  // 根据管理后台水印配置构建水印文本
  const now = new Date();
  const timestamp = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
  const wmParts: string[] = [];
  if (wmConfig?.WM_USEACCOUNT_SHOW && user?.email) wmParts.push(user.email);
  if (wmConfig?.WM_COMPANY_SHOW && user?.company) wmParts.push(user.company);
  if (wmConfig?.WM_ACCESSDATE_SHOW) wmParts.push(timestamp);
  const watermarkText = wmParts.join('  ·  ');

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!material) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div
        className="video-container bg-black rounded-2xl overflow-hidden flex flex-col w-full max-w-5xl"
        style={{
          maxHeight: '92vh',
          boxShadow: '0 32px 64px rgba(0,0,0,0.5)',
          animation: 'modalIn 0.2s ease-out',
        }}
        onMouseMove={resetHideTimer}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3.5 shrink-0 transition-all duration-300"
          style={{ background: 'rgba(0,0,0,0.7)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-7 h-7 flex items-center justify-center rounded-full shrink-0" style={{ background: '#0069BD' }}>
              <i className="ri-play-fill text-white text-xs"></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-white truncate">{material.title}</h2>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                <p className="text-xs text-white/50">{material.fileSize} · {material.type} Document</p>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50/15 text-amber-400 border border-amber-400/30 whitespace-nowrap">
                  <i className="ri-shield-check-line text-xs"></i>Watermark Protected
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            {user && (
              <button
                type="button"
                onClick={() => setShowFeedback(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white border border-white/20 hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-feedback-line text-xs"></i>
                <span className="hidden sm:inline">Feedback</span>
              </button>
            )}
            {/* Download 按钮：根据管理后台 downloadAllowed 控制 */}
            {material.downloadAllowed ? (
              <button
                onClick={() => {
                  const token = localStorage.getItem('agent_token');
                  const params = new URLSearchParams({ id: material.id });
                  if (token) params.set('token', token);
                  window.open(`/api/agentfront/download.htm?${params.toString()}`, '_blank');
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white border border-white/20 hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-line text-xs"></i>
                <span className="hidden sm:inline">Download</span>
              </button>
            ) : (
              <div
                className="relative group/tip"
                onMouseEnter={() => showTip('Download not available for this video')}
                onMouseLeave={hideTip}
              >
                <button
                  disabled
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white/30 border border-white/10 cursor-not-allowed whitespace-nowrap"
                >
                  <i className="ri-download-line text-xs"></i>
                  <span className="hidden sm:inline">Download</span>
                </button>
                {showTooltip && (
                  <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap border border-white/10 z-10">
                    {tooltipText}
                    <div className="absolute top-full right-4 w-0 h-0" style={{ borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid #111827' }}></div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Video Area */}
        <div
          className="relative flex-1 bg-black overflow-hidden cursor-pointer flex items-center justify-center"
          style={{ minHeight: '360px' }}
          onClick={togglePlay}
        >
          <video
            ref={videoRef}
            src={material.fileUrl}
            className="max-w-full max-h-full object-contain"
            controlsList="nodownload"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            poster={material.thumbnail}
            preload="metadata"
          />

          {/* 动态水印遮罩 */}
          {watermarkText && <WatermarkOverlay text={watermarkText} dark />}

          {/* Center play/pause indicator */}
          {!playing && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full"
                style={{ background: 'rgba(0,105,189,0.85)', backdropFilter: 'blur(4px)' }}
              >
                <i className="ri-play-fill text-white text-4xl ml-1"></i>
              </div>
            </div>
          )}


        </div>

        {/* Controls Bar */}
        <div
          className="shrink-0 px-4 pb-4 pt-3 transition-all duration-300"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.7))' }}
        >
          {/* Progress Bar */}
          <div
            ref={progressRef}
            className="relative h-1.5 rounded-full mb-3 cursor-pointer group/progress"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onClick={handleProgressClick}
          >
            {/* Buffered */}
            <div
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${buffered}%`, background: 'rgba(255,255,255,0.25)' }}
            />
            {/* Played */}
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all"
              style={{ width: `${progress}%`, background: '#0069BD' }}
            />
            {/* Thumb */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white opacity-0 group-hover/progress:opacity-100 transition-opacity"
              style={{ left: `calc(${progress}% - 7px)`, boxShadow: '0 0 4px rgba(0,0,0,0.4)' }}
            />
          </div>

          {/* Controls Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                className="w-9 h-9 flex items-center justify-center rounded-full text-white hover:bg-white/10 transition-colors cursor-pointer"
              >
                <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-xl`}></i>
              </button>

              {/* Volume */}
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleMute}
                  className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer"
                >
                  <i className={`${muted || volume === 0 ? 'ri-volume-mute-line' : volume < 0.5 ? 'ri-volume-down-line' : 'ri-volume-up-line'} text-base`}></i>
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={muted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 rounded-full cursor-pointer accent-white"
                  style={{ accentColor: '#0069BD' }}
                />
              </div>

              {/* Time */}
              <span className="text-xs text-white/60 font-mono tabular-nums">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                onMouseEnter={() => showTip(fullscreen ? 'Exit Fullscreen' : 'Fullscreen')}
                onMouseLeave={hideTip}
                className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer relative"
              >
                <i className={`${fullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {showFeedback && material && (
        <FeedbackModal material={material} onClose={() => setShowFeedback(false)} />
      )}
    </div>
  );
}
